/* eslint-disable no-console */
import {
  PROFILERROR_COOKIE_NAME,
  LASTSHOW_COOKIE_NAME,
  TESTPERSIST_COOKIE_NAME,
  VARIANT,
  PAGE_PURPOSES,
  PAGE_VENDORS,
  PURPOSE_CONTENT,
  PURPOSE_ADS,
  USER_INT,
  LASTSHOW_IMAGINARY_NAME
} from '../constants';
import {demoMode} from '../utils/urlParams';
import {inProfile} from '../utils/scriptParams';
import {getCookie} from './cookie';
import {getConsentCookie} from './consent';
import {getRandomString} from '../utils/randomString';
import {logger} from './logger';

const SERVICE = 'partner3';
const ACTION = 'event';
const LOGGER_LABEL = 'CMP:DOT';
const CMP = true;
const HASH = getRandomString();
const isStorageSupported = window.localStorage && window.localStorage.getItem && window.localStorage.setItem;

// log order
let order = 0;

class DOT {
  constructor() {
    this.disabled = inProfile() || demoMode();
  }

  /**
   * Configures DOT script and sends impress.
   */
  config(userType, rusId = null, lazyLoad = false) {
    try {
      if (this.disabled) {
        return;
      }

      if (!this.instance) {
        this.instance = window.DOT.getNewInstance();
      }

      // Distinguish internal/external dialog
      const serviceVariant = userType === USER_INT ? 'int' : 'ext';

      const config = {
        service: SERVICE,
        serviceVariant,
        lazyLoadingEnabled: lazyLoad,
        impress: false,
        mousedown: false,
        cmp: CMP,
        cmpVersion: process.env._CMP_VERSION
      };

      if (!lazyLoad && rusId) {
        config.rusId = rusId;
      }

      this.instance.cfg(config, () => {
        logger(1100, 'impress', config, LOGGER_LABEL);
      });
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Sends hit.
   * @param {string} action
   * @param {Object} data
   */
  _hit(action, data) {
    try {
      data.d.order = ++order;
      data.d.timestamp = Date.now();
      data.d.cmppersisttestcookie = getCookie(TESTPERSIST_COOKIE_NAME);
      data.d.cmplastshowcookie = getCookie(LASTSHOW_COOKIE_NAME).replace('|', ':');
      if (isStorageSupported) {
        // log cmplastshowcookie from localStorage
        const value = window.localStorage.getItem(LASTSHOW_COOKIE_NAME) || '';
        data.d.cmplastshowlocalstorage = value.replace('|', ':');
      }
      data.d.cmpwtf = getCookie(LASTSHOW_IMAGINARY_NAME).replace('|', ':');
      data.d.euconsent = getConsentCookie();
      if (this.instance._cfg.serviceVariant === 'int') {
        data.d.cmpprofilerrorcookie = getCookie(PROFILERROR_COOKIE_NAME).replace('|', ':');
      }
      data.d.session = HASH;
      data.d.cmp = CMP;
      data.d.cmpVersion = process.env._CMP_VERSION;
      this.instance.hit(action, data, () => {
        logger(1101, 'hit', {action, data}, LOGGER_LABEL);
      });
    } catch (error) {
      console.error(error);
    }
  }

  hit(data) {
    if (this.disabled) {
      return;
    }

    try {
      this.instance.hit(
        ACTION,
        {
          d: {
            breadcrumbs: data,
            cmpVersion: process.env._CMP_VERSION
          }
        },
        () => {
          logger(1102, 'hit', {ACTION, data}, LOGGER_LABEL);
        }
      );
    } catch (error) {
      console.error(error);
    }
  }

  start() {
    if (this.disabled) {
      return;
    }
    this._hit(ACTION, {
      d: {
        variant: VARIANT,
        action: 'start'
      }
    });
  }

  end(steps) {
    if (this.disabled) {
      return;
    }
    this._hit(ACTION, {
      d: {
        variant: VARIANT,
        action: 'end',
        steps
      }
    });
  }

  /**
   * Sends info that the user switched to the page.
   * @param {string} page
   */
  navPage(page) {
    if (this.disabled) {
      return;
    }
    if (page === PAGE_PURPOSES) {
      this._hit(ACTION, {d: {variant: VARIANT, action: 'navigate-purposes'}});
    } else if (page === PAGE_VENDORS) {
      this._hit(ACTION, {d: {variant: VARIANT, action: 'navigate-vendors'}});
    }
  }

  /**
   * Sends info that the user clicked submit on the screen.
   * @param {string} screen
   */
  submitClick(screen, seznamAllowed, contentAllowed, adsAllowed) {
    if (this.disabled) {
      return;
    }
    this._hit(ACTION, {
      d: {
        variant: VARIANT,
        action: 'submitClick',
        screen,
        // eslint-disable-next-line camelcase
        seznam_allowed: seznamAllowed,
        [PURPOSE_ADS]: adsAllowed,
        [PURPOSE_CONTENT]: contentAllowed
      }
    });
  }

  /**
   * Sends info that cookie was saved in browser.
   */
  cookieSaved() {
    if (this.disabled) {
      return;
    }
    this._hit(ACTION, {
      d: {
        variant: VARIANT,
        action: 'saved'
      }
    });
  }

  /**
   * Sends info that euconsent was saved into profile.
   */
  profileSaved() {
    if (this.disabled) {
      return;
    }
    this._hit(ACTION, {
      d: {
        variant: VARIANT,
        action: 'profileSaved'
      }
    });
  }

  /**
   * Sends info that euconsent was saved into profile.
   */
  profileError() {
    if (this.disabled) {
      return;
    }
    this._hit(ACTION, {
      d: {
        variant: VARIANT,
        action: 'profileError'
      }
    });
  }

  /**
   * Sends info that dialog was opened.
   */
  show(steps) {
    if (this.disabled) {
      return;
    }
    this._hit(ACTION, {
      d: {
        variant: VARIANT,
        action: 'show',
        steps
      }
    });
  }

  /**
   * Sends info that the user exited dialog.
   */
  exit(screen) {
    if (this.disabled) {
      return;
    }
    this._hit(ACTION, {
      d: {
        variant: VARIANT,
        action: 'exit',
        screen
      }
    });
  }

  /**
   * Sends info that euconsent string is not made by Seznam.
   */
  foreignCookie() {
    if (this.disabled) {
      return;
    }
    this._hit(ACTION, {
      d: {
        variant: VARIANT,
        action: 'foreign-euconsent'
      }
    });
  }

  /**
   * Sends info about nextShow interval
   * @param {string} type set or get
   * @param {Array} data
   */
  nextShow(type, data) {
    if (this.disabled) {
      return;
    }

    const action = type === 'set' ? 'setNextShow' : 'getNextShow';

    let nextShow = null;
    let stage = null;
    let date = null;

    if (data) {
      data = data.split('|');
      nextShow = parseInt(data[0], 10);
      stage = parseInt(data[1], 10);
      date = new Date(nextShow).toString();
    }

    this._hit(ACTION, {
      d: {
        variant: VARIANT,
        action,
        stage,
        value: nextShow,
        date
      }
    });
  }

  /**
   * Log setting nextShow
   * @param {Array} data
   */
  setNextShow(data) {
    this.nextShow('set', data);
  }

  /**
   * Log getting nextShow
   * @param {Array} data
   */
  getNextShow(data) {
    this.nextShow('get', data);
  }

  /**
   * Sends info that webview was detected.
   */
  webview() {
    if (this.disabled) {
      return;
    }
    this._hit(ACTION, {
      d: {
        variant: VARIANT,
        action: 'webview'
      }
    });
  }
}

export default new DOT();
